import { useUtilityServices } from "@features/bx/bulk-bill-generation/hooks";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Input } from "@shared/ui/input";
import {
  Check,
  ChevronDown,
  Download,
  Edit,
  FileText,
  Loader2,
  Plus,
  Save,
  Upload,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import {
  useGlCodesGrouped,
  useUpdateGlCode,
  useImportCsvGlCodes,
  useChoices,
} from "../../hooks"; // Adjust import path as needed
import { logEvent } from "@shared/analytics/analytics";
import { Dashboard } from "../dashboard/dashboard";
import GlCodeList from "../list/list";
import { BulkImportModal } from "../create/BulkImportModal";
import { PreviewImportModal } from "../create/PreviewImportModal";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PAGE_SIZE } from "@shared/utils/constants";

// Custom Switch component for status toggle
const StatusSwitch = ({ checked, onToggle, disabled = false }) => {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${checked ? "bg-blue-600" : "bg-gray-200"}
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      <span
        className={`
          inline-block h-5 w-5 transform rounded-full bg-white transition-transform
          ${checked ? "translate-x-5" : "translate-x-0.5"}
        `}
      />
    </button>
  );
};

const GLEntityDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [errorCount, setErrorCount] = useState<number>(0);

  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentStatus = searchParams.get("status") || "";
  const currentGlType = searchParams.get("gl_type") || "";

  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1,
  );
  const itemsPerPage = PAGE_SIZE || 10;

  const queryParams = useMemo(
    () => ({
      search: currentSearch || undefined,
      status: currentStatus || undefined,
      gl_type: currentGlType || undefined,
      page: currentPage,
      limit: itemsPerPage,
    }),
    [currentSearch, currentStatus, currentGlType, currentPage, itemsPerPage],
  );

  const { data: glCodesResponse, isLoading } = useGlCodesGrouped(queryParams);
  const { data: glTypesData } = useChoices("gl-types");
  const { mutateAsync: importCsv, isPending: isImporting } =
    useImportCsvGlCodes();

  const handleSearch = (searchTerm: string) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }
    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleApplyFilters = (data: any) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    if (data.status) newParams.status = data.status;
    else delete newParams.status;

    if (data.gl_type) newParams.gl_type = data.gl_type;
    else delete newParams.gl_type;

    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    delete newParams.status;
    delete newParams.gl_type;
    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
    form.reset({
      status: "",
      gl_type: "",
    });
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  const statusOptions = [
    { value: "1", label: "Active" },
    { value: "2", label: "Inactive" },
  ];

  const glTypesOptions = useMemo(
    () =>
      (glTypesData || []).map((choice: any) => ({
        value: choice.value?.toString(),
        label: choice.label,
      })),
    [glTypesData],
  );

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "status",
        type: "select",
        label: "Status",
        placeholder: "All Status",
        disabled: isLoading,
        options: statusOptions,
      },
      {
        name: "gl_type",
        type: "select",
        label: "GL Type",
        placeholder: "All GL Types",
        disabled: isLoading,
        options: glTypesOptions,
      },
    ],
    [isLoading, glTypesOptions],
  );

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: {
      status: currentStatus,
      gl_type: currentGlType,
    },
  });

  const filterPanel = (
    <div className="space-y-4">
      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={handleApplyFilters}
        onReset={handleResetFilters}
        config={{
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear Filters",
          classes: {
            buttonContainer: "justify-end",
          },
        }}
        gridColumns={2}
        className="w-full"
      />
    </div>
  );

  return (
    <div className="">
      <div>
        <div className="flex flex-col xl:flex-row justify-between xl:items-center gap-8">
          <div>
            <PageHeader
              title="GL Code Management"
              subtitle="Hierarchical chart of accounts across all utility services and financial categories."
            />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowImportModal(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Import CSV
            </Button>
            <Button
              variant="default"
              onClick={() => navigate("/bx/gl-code/create")}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add GL Code
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Summary Cards - Dashboard */}
      <Dashboard kpi={glCodesResponse?.kpi} />

      {/* Generic Table */}
      <div className="mt-4">
        <GlCodeList
          data={
            glCodesResponse?.result?.result || glCodesResponse?.result || []
          }
          totalItems={glCodesResponse?.result?.count || 0}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          isLoading={isLoading}
          filterPanel={filterPanel}
          onSearch={handleSearch}
        />
        {showImportModal && (
          <BulkImportModal
            open={showImportModal}
            onOpenChange={setShowImportModal}
            onImportSuccess={(file, response) => {
              setUploadedFile(file);

              const result = response?.result || [];
              const errors = response?.errors || [];
              const camelCaseData = result.map((item: any) => ({
                id: item.id,
                glCode: item.glCode,
                glName: item.glName,
                glTypeDisplay: item.glTypeDisplay,
                sourceDisplay: item.sourceDisplay,
                transactionCategoryDisplay: item.transactionCategoryDisplay,
                transactionType: item.transactionType,
                statusDisplay: item.statusDisplay,
              }));

              setPreviewData(camelCaseData);
              setErrorCount(errors.length);
              setShowImportModal(false);
              setShowPreviewModal(true);
            }}
          />
        )}

        {showPreviewModal && (
          <PreviewImportModal
            open={showPreviewModal}
            onOpenChange={setShowPreviewModal}
            file={uploadedFile}
            data={previewData}
            errorCount={errorCount}
            onRemoveFile={() => {
              setUploadedFile(null);
              setPreviewData([]);
              setErrorCount(0);
              setShowPreviewModal(false);
              setShowImportModal(true);
            }}
            onImport={async () => {
              if (!uploadedFile) return;
              try {
                await importCsv({
                  file: uploadedFile,
                  action: "save",
                });
                setShowPreviewModal(false);
                setUploadedFile(null);
                setPreviewData([]);
                setErrorCount(0);
                queryClient.refetchQueries({
                  queryKey: [QueryKeyFactory.module.bx.glCode.glCodeList()],
                });
                queryClient.refetchQueries({
                  queryKey: [QueryKeyFactory.module.bx.glCode.summary()],
                });
              } catch (error) {
                console.error("Failed to save import:", error);
              }
            }}
            isImporting={isImporting}
          />
        )}
      </div>
    </div>
  );
};

export default GLEntityDashboard;
